import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Button from '@mui/material/Button';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export const CORES = {
    marca: '#0b243d',
    destaque: '#ffc845',
    serie: '#2a78d6',
    tinta: '#0b0b0b',
    tintaSecundaria: '#52514e',
    tintaFraca: '#898781',
    grade: '#e1e0d9',
    eixo: '#c3c2b7',
    bom: '#0ca30c',
    bomTexto: '#006300',
    alerta: '#fab219',
    critico: '#d03b3b',
    neutro: '#a8a7a0',
};

export const TONS = {
    good: { cor: CORES.bom, fundo: '#e0f7e9', texto: '#2e7d32' },
    warning: { cor: CORES.alerta, fundo: '#fff4dc', texto: '#8a5a00' },
    critical: { cor: CORES.critico, fundo: '#fdecea', texto: '#c62828' },
    neutral: { cor: CORES.neutro, fundo: '#f0efec', texto: CORES.tintaSecundaria },
};

// Opções base dos gráficos de barra: marcas finas, grade discreta, uma cor por série
export function opcoesBarra({ horizontal = false, formatarValor = (v) => v, onClick } = {}) {
    const eixoValor = {
        beginAtZero: true,
        grid: { color: CORES.grade },
        border: { display: false },
        ticks: { color: CORES.tintaFraca, precision: 0 },
    };
    const eixoCategoria = {
        grid: { display: false },
        border: { color: CORES.eixo },
        ticks: { color: CORES.tintaSecundaria },
    };
    return {
        indexAxis: horizontal ? 'y' : 'x',
        responsive: true,
        maintainAspectRatio: false,
        onClick,
        onHover: onClick ? (e, els) => { e.native.target.style.cursor = els.length ? 'pointer' : 'default'; } : undefined,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: '#ffffff',
                titleColor: CORES.tinta,
                bodyColor: CORES.tintaSecundaria,
                borderColor: CORES.grade,
                borderWidth: 1,
                padding: 10,
                displayColors: false,
                callbacks: { label: (ctx) => formatarValor(ctx.parsed[horizontal ? 'x' : 'y'], ctx) },
            },
        },
        scales: horizontal ? { x: eixoValor, y: eixoCategoria } : { x: eixoCategoria, y: eixoValor },
    };
}

export function datasetBarra(label, data, cor = CORES.serie) {
    return {
        label,
        data,
        backgroundColor: cor,
        hoverBackgroundColor: '#1c5cab',
        borderRadius: 4,
        borderSkipped: 'start',
        maxBarThickness: 32,
    };
}

export function KpiCard({ titulo, valor, detalhe, tomDetalhe, loading, erro, onClick }) {
    return (
        <Paper
            variant="outlined"
            onClick={onClick}
            sx={{
                p: 2.5,
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                cursor: onClick ? 'pointer' : 'default',
                transition: 'border-color .2s, box-shadow .2s',
                '&:hover': onClick ? { borderColor: CORES.marca, boxShadow: '0 4px 12px rgba(0,0,0,0.06)' } : undefined,
            }}
        >
            <Typography variant="body2" sx={{ color: CORES.tintaSecundaria, fontWeight: 500 }}>
                {titulo}
            </Typography>
            {loading ? (
                <Skeleton width="60%" height={40} />
            ) : erro ? (
                <Typography variant="body2" sx={{ color: TONS.critical.texto, display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    <ErrorOutlineIcon fontSize="small" /> Indisponível
                </Typography>
            ) : (
                <>
                    <Typography sx={{ fontSize: 28, fontWeight: 700, color: CORES.tinta, lineHeight: 1.2 }}>
                        {valor}
                    </Typography>
                    {detalhe && (
                        <Typography variant="caption" sx={{ color: tomDetalhe ? TONS[tomDetalhe].texto : CORES.tintaFraca }}>
                            {detalhe}
                        </Typography>
                    )}
                </>
            )}
        </Paper>
    );
}

export function Painel({ titulo, subtitulo, acao, onAcao, loading, erro, vazio, mensagemVazio, altura, children, sx }) {
    let conteudo = children;
    if (loading) {
        conteudo = <Skeleton variant="rounded" height={altura || 220} />;
    } else if (erro) {
        conteudo = (
            <Box sx={{ height: altura || 'auto', py: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: TONS.critical.texto }}>
                <ErrorOutlineIcon fontSize="small" />
                <Typography variant="body2">Não foi possível carregar estes dados.</Typography>
            </Box>
        );
    } else if (vazio) {
        conteudo = (
            <Box sx={{ height: altura || 'auto', py: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" sx={{ color: CORES.tintaFraca }}>{mensagemVazio || 'Sem dados ainda.'}</Typography>
            </Box>
        );
    } else if (altura) {
        conteudo = <Box sx={{ position: 'relative', height: altura }}>{children}</Box>;
    }

    return (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', ...sx }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 16, color: CORES.tinta }}>{titulo}</Typography>
                    {subtitulo && (
                        <Typography variant="caption" sx={{ color: CORES.tintaFraca }}>{subtitulo}</Typography>
                    )}
                </Box>
                {acao && (
                    <Button size="small" onClick={onAcao} sx={{ textTransform: 'none', color: CORES.marca, whiteSpace: 'nowrap' }}>
                        {acao}
                    </Button>
                )}
            </Box>
            <Box sx={{ flex: 1 }}>{conteudo}</Box>
        </Paper>
    );
}

export function StatusTag({ tom = 'neutral', children }) {
    const t = TONS[tom];
    return (
        <Box component="span" sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            px: 1.25, py: 0.25, borderRadius: 4, fontSize: 12, fontWeight: 600,
            background: t.fundo, color: t.texto, whiteSpace: 'nowrap',
        }}>
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', background: t.cor }} />
            {children}
        </Box>
    );
}

// Linha com rótulo, valor e barra proporcional (usada no breakdown de status)
export function LinhaProporcao({ label, valor, total, tom = 'neutral' }) {
    const pct = total ? (valor / total) * 100 : 0;
    return (
        <Box sx={{ mb: 1.75 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <StatusTag tom={tom}>{label}</StatusTag>
                <Typography variant="body2" sx={{ color: CORES.tinta, fontVariantNumeric: 'tabular-nums' }}>
                    <strong>{valor}</strong>
                    <Box component="span" sx={{ color: CORES.tintaFraca, ml: 1 }}>{pct.toFixed(0)}%</Box>
                </Typography>
            </Box>
            <Box sx={{ height: 8, borderRadius: 4, background: '#f0efec', overflow: 'hidden' }}>
                <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: TONS[tom].cor }} />
            </Box>
        </Box>
    );
}
